import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DeviceMobile, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaregiverSmsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [phone, setPhone] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/caregiver-profiles/me`, { headers: { Authorization: `Bearer ${token}` } });
      const p = res.data || {};
      setOptIn(!!p.sms_opt_in);
      setPhone(p.phone || '');
    } catch (e) {
      // No profile - hide card
      setOptIn(null);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/caregiver-profiles/me/sms-preferences`,
        { sms_opt_in: optIn, phone: phone || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhone(res.data.phone || '');
      toast.success(optIn ? 'You will now receive open-shift text alerts' : 'SMS alerts turned off');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || optIn === null) return null;

  return (
    <Card className="rounded-2xl border border-border" data-testid="caregiver-sms-settings">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <DeviceMobile weight="duotone" className="w-5 h-5 text-primary" />
          Open Shift Text Alerts
        </CardTitle>
        <CardDescription>Get texted when a shift opens near you — reply <strong>YES</strong> to request it right from your phone, no app needed.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl border border-border p-3 bg-muted/20">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            data-testid="sms-opt-in-toggle"
          />
          <div className="flex-1">
            <p className="text-sm font-medium">Send me a text when an open shift is offered</p>
            <p className="text-xs text-muted-foreground mt-0.5">You&apos;ll still get in-app notifications and email. You can turn this off anytime.</p>
          </div>
        </label>
        <div className="space-y-1">
          <Label className="text-xs">Mobile Number (US)</Label>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="rounded-xl"
            disabled={!optIn}
            data-testid="sms-phone-input"
          />
          <p className="text-[11px] text-muted-foreground">Any US format works — we&apos;ll normalize it. Standard message &amp; data rates apply.</p>
        </div>
        <Button className="w-full rounded-xl gap-2" onClick={save} disabled={saving} data-testid="save-sms-prefs-btn">
          <CheckCircle weight="bold" className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CaregiverSmsSettings;
